import {
  type BusinessWithId,
  type OwnerWithId,
  type Public,
} from '@/shared/schema';
import {
  type AdminSettings,
  type MarketingConfig,
} from '@/shared/schema/admin';
import {
  type Unsubscribe,
  type User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { getDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { create } from 'zustand';
import { auth } from '../firebase/firebase';
import { OwnerService } from '../firebase/services';
import {
  adminSettingsRef,
  marketingConfigRef,
  publicRef,
} from '../firebase/services/helpers/firestore-refs';
import { AnalyticsService } from '@/shared/analytics/analytics-service';

interface AuthStorage {
  user: User | null;
  profile: OwnerWithId | null;
  publicDoc: Public | null;
  activeBusiness: BusinessWithId | null;
  adminSettings: AdminSettings | null;
  marketingConfig: MarketingConfig | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => void;
  unsubscribe: Unsubscribe | null;
  status: 'idle' | 'loading' | 'unauthenticated' | 'loggedIn' | 'error';
  error_message: string | null;
  clear_session: () => void;
  refreshProfile: () => Promise<void>;
}

export const useAuth = create<AuthStorage>((set, get) => ({
  user: null,
  profile: null,
  publicDoc: null,
  unsubscribe: null,
  status: 'idle',
  error_message: null,
  activeBusiness: null,
  adminSettings: null,
  marketingConfig: null,

  login: async (email, password) => {
    try {
      set({ status: 'loading', error_message: null });
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: any) {
      let errorMessage = 'Credenciales incorrectas o error de red.';
      if (
        e.code === 'auth/user-not-found' ||
        e.code === 'auth/wrong-password' ||
        e.code === 'auth/invalid-credential'
      ) {
        errorMessage = 'El correo o la contraseña son incorrectos.';
      }
      set({ status: 'error', error_message: errorMessage });
      toast.error(errorMessage);
    }
  },

  logout: async () => {
    try {
      set({ status: 'loading' });
      await auth.signOut();
      get().clear_session();
    } catch (e) {
      const error = e instanceof Error ? e.message : 'Logout Failed';
      set({ status: 'error', error_message: error });
    }
  },

  initialize: () => {
    if (get().unsubscribe) {
      console.log('[AUTH_INIT] Already subscribed, skipping.');
      return;
    }
    console.log('[AUTH_INIT] Starting auth initialization...');
    set({ status: 'loading' });

    const sub = onAuthStateChanged(auth, async (user) => {
      console.log('[AUTH_INIT] onAuthStateChanged fired. User:', user ? user.uid : 'null');
      if (user) {
        try {
          console.log('[AUTH_INIT] Step 1/8: Fetching owner profile for uid:', user.uid);
          let owner = await OwnerService.getById(user.uid);
          console.log('[AUTH_INIT] Step 1/8 DONE. Owner found:', !!owner);

          if (!owner) {
            console.warn('[AUTH_INIT] No owner doc found. Setting profile=null, status=loggedIn.');
            set({
              user,
              profile: null,
              publicDoc: null,
              activeBusiness: null,
              status: 'loggedIn',
            });
            return;
          }

          // In the new model, owner and business are same or linked directly
          let businessContext: any = owner.account_type === 'business' ? owner : null;
          const ownerId = owner.id;
          console.log('[AUTH_INIT] Step 2/8: ownerId resolved:', ownerId, '| account_type:', owner.account_type);

          // Fetch admin data
          console.log('[AUTH_INIT] Step 3/8: Fetching adminSettings + marketingConfig...');
          const [adminSettingsSnap, marketingConfigSnap] = await Promise.all([
            getDoc(adminSettingsRef(ownerId)),
            getDoc(marketingConfigRef(ownerId)),
          ]);
          console.log('[AUTH_INIT] Step 3/8 DONE. adminSettings exists:', adminSettingsSnap.exists(), '| marketingConfig exists:', marketingConfigSnap.exists());

          // public/{ownerId} may not have read permissions for non-admins;
          // fetch it gracefully so a permission error doesn't block auth.
          console.log('[AUTH_INIT] Step 4/8: Fetching public doc...');
          let publicSnap;
          try {
            publicSnap = await getDoc(publicRef(ownerId));
            console.log('[AUTH_INIT] Step 4/8 DONE. public exists:', publicSnap.exists());
          } catch (e: any) {
            console.warn('[AUTH_INIT] Step 4/8 FAILED (graceful). publicRef error:', e.message || e);
            publicSnap = null;
          }

          const adminSettings: AdminSettings | null = adminSettingsSnap.exists()
            ? (adminSettingsSnap.data() as AdminSettings)
            : null;

          const marketingConfig: MarketingConfig | null =
            marketingConfigSnap.exists()
              ? (marketingConfigSnap.data() as MarketingConfig)
              : null;
          
          const publicDoc: Public | null = publicSnap?.exists?.()
            ? (publicSnap.data() as Public)
            : null;

          console.log('[AUTH_INIT] Step 5/7: Setting auth state to loggedIn...');
          set({
            user,
            profile: owner,
            publicDoc,
            activeBusiness: businessContext,
            adminSettings,
            marketingConfig,
            status: 'loggedIn',
          });
          console.log('[AUTH_INIT] Step 5/7 DONE. Auth state = loggedIn.');

          // 🛡️ Identificar en Sentry/Analytics
          console.log('[AUTH_INIT] Step 6/7: Identifying in Analytics...');
          AnalyticsService.identify(user.uid, {
            email: owner.email || user.email,
            username: owner.public.branding.name,
            account_type: owner.account_type,
            businessId: businessContext?.id,
            plan: adminSettings?.plan,
            slug: owner.public.branding.slug,
          });
          console.log('[AUTH_INIT] Step 6/7 DONE. Analytics identified.');

        } catch (error: any) {
          console.error('[AUTH_INIT] ❌ FATAL ERROR during auth init:', error?.message || error);
          console.error('[AUTH_INIT] Stack:', error?.stack);
          set({ user, profile: null, publicDoc: null, activeBusiness: null, status: 'error', error_message: error?.message || 'Unknown auth error' });
        }
      } else {
        console.log('[AUTH_INIT] No user detected. Clearing session.');
        get().clear_session();
      }
    });

    set({ unsubscribe: sub });
  },

  refreshProfile: async () => {
    const user = get().user;
    if (!user) return;

    try {
      let owner = await OwnerService.getById(user.uid);
      if (!owner) return;

      let businessContext: any = owner.account_type === 'business' ? owner : null;

      const ownerId = owner.id;
      const [
        adminSettingsSnap,
        marketingConfigSnap,
        publicSnap,
      ] = await Promise.all([
        getDoc(adminSettingsRef(ownerId)),
        getDoc(marketingConfigRef(ownerId)),
        getDoc(publicRef(ownerId)),
      ]);

      const adminSettings: AdminSettings | null = adminSettingsSnap.exists()
        ? (adminSettingsSnap.data() as AdminSettings)
        : null;

      const marketingConfig: MarketingConfig | null =
        marketingConfigSnap.exists()
          ? (marketingConfigSnap.data() as MarketingConfig)
          : null;
      
      const publicDoc: Public | null = publicSnap.exists()
        ? (publicSnap.data() as Public)
        : null;

      set({
        profile: owner,
        publicDoc,
        activeBusiness: businessContext,
        adminSettings,
        marketingConfig,
      });

      AnalyticsService.identify(user.uid, {
        email: owner.email || user.email,
        username: owner.public.branding.name,
        account_type: owner.account_type,
        businessId: businessContext?.id,
        plan: adminSettings?.plan,
        slug: owner.public.branding.slug,
      });
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  },

  clear_session: () => {
    console.log('[AUTH_INIT] clear_session called. Setting status=unauthenticated.');
    set({
      profile: null,
      publicDoc: null,
      user: null,
      adminSettings: null,
      marketingConfig: null,
      status: 'unauthenticated',
      error_message: null,
    });
  },
}));
